/**
 * HITL approval panels for the Campaign Copilot orchestration workflow.
 *
 * The orchestration SOP (`RockyAgentCapabilities/skills/campaign_copilot_agent/
 * campaign-orchestration/SKILL.md`) declares four AG-UI frontend tools:
 *
 *   Gate 1  — `approve_audience_segment` — on the audience preview, before it is saved
 *   Gate 2  — `approve_email_content`    — before `create_octopus_program`
 *   Generic — `request_user_approval`    — every other user-confirmation branch
 *              (tool retry/skip, partial-success continue-vs-retry, destructive
 *              proceed, …). N-choice (2–4 buttons), returns `{ choice }`.
 *   Step 3a — `review_email_preview`   — sample-email preview before the batch;
 *              returns `{ action }`. Lives in `PreviewReviewPanel.tsx`.
 *
 * These are PLAIN presentational components. AgUiChatPanel registers the tools
 * via `useHumanInTheLoop` (CopilotKit v2) — the `render` prop drives a discriminated
 * `status` union: `InProgress` (args streaming) / `Executing` (interactive,
 * `respond()` available) / `Complete` (settled, `result` string available).
 * `useRenderToolCall()` walks `agent.messages` and invokes the registered
 * `render` per tool call, so the card lives with the assistant message that
 * owns it — persistent across settle, rehydrated on session load, no local
 * pendingApproval state required.
 *
 * Components exported:
 * - `AudienceApprovalPanel` / `ContentApprovalPanel` / `GenericApprovalPanel`
 *   — the interactive (Executing) variants.
 * - `SettledApprovalCard` — the read-only variant for Complete state; shows a
 *   verdict pill (✅ 已批准 / ❌ 已取消 / ⚪ 已处理) instead of buttons. Gate 2
 *   instead re-renders `ContentApprovalPanel` with a `verdict`, so the rows the
 *   operator approved stay on the card.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Textarea from '@amzn/awsui-components-react/polaris/textarea';
import Input from '@amzn/awsui-components-react/polaris/input';
import { parseCsv } from '../../ReportViewer/parseCsv';
import MarkdownRenderer from '../MarkdownRenderer';
import { WorkspaceFileLink } from './WorkspaceFileLink';

/** Rows per page in each Gate 2 table (the ≤20 sample, the ≤50 flagged). */
const CONTENT_PAGE_SIZE = 10;

// ─── Gate 1: Audience Segment approval ───────────────────────────────────
//
// Gate 1 no longer renders the sample rows itself. The delegated
// `audience-segmentation` skill already prints a count + ≤20-row sample
// table in the chat stream (per its own SKILL.md rule), so duplicating it
// in the card produced two copies of the same data and lost the audit
// trail whenever the card unmounted on click. The card is now a slim
// confirmation surface — count + one-line description + 批准/调整条件/取消 — and
// the persistent sample display lives in chat above the card.

export interface AudienceApprovalPanelProps {
  /** One-line human-readable description (usually the original NL query). */
  segmentDescription?: string;
  /** How many people the preview matched (the preview COUNT). */
  memberCount?: number;
  /** Whether the run is still waiting on this approval — buttons disable when false. */
  active: boolean;
  onApprove: () => void;
  onReject: () => void;
  /**
   * When provided, a「调整条件」button opens a modal for new criteria in natural
   * language; submitting calls this with the trimmed text. Opening, cancelling or
   * ✕-ing the modal answers nothing. Omit to hide the button.
   */
  onAdjust?: (adjustment: string) => void;
  /**
   * When provided AND `memberCount > 20`, a「👁️ 看全部人的信息」button appears (≤20
   * the sample already is everyone). No modal: a click calls this directly.
   */
  onViewAll?: () => void;
  /**
   * Agent-supplied examples for THIS audience, shown as the 调整条件 modal's
   * placeholder (non-empty trimmed strings, first 3). None → no placeholder.
   */
  adjustExamples?: string[];
}

/**
 * Deliberately carries NO sample rows.
 *
 * Reasoning carried over from mainline `d85a9d0`, which measured what the earlier
 * version actually did: the card sits directly beneath the preview table the
 * segmentation skill already printed, so a second copy was pure duplication — and
 * it did not even render as rows. The list keyed off `customer_id` while the skill
 * emits `octopus_customer_id`, so every entry fell through to
 * `JSON.stringify(row)` and one ~200-char blob per person overflowed the card.
 *
 * Dropping the parameter also means the agent no longer re-emits eight enriched
 * profile rows verbatim as tool-call arguments. Those arguments ride in
 * `messages[]` on every subsequent turn, which is the frame budget
 * `messageWindowMiddleware` exists to fight.
 *
 * ⚠️ Unrelated to `sample_octopus_customer_ids` in the saved record — a storage
 * field the materialization step still requires — and to the preview table in
 * chat, which the domain requires the operator to have seen. Neither is affected.
 */
export function AudienceApprovalPanel({
  segmentDescription,
  memberCount,
  active,
  onApprove,
  onReject,
  onAdjust,
  onViewAll,
  adjustExamples,
}: AudienceApprovalPanelProps) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustment, setAdjustment] = useState('');
  // A settled or inactive card never keeps the modal on screen.
  const modalOpen = active && adjustOpen;
  const closeModal = () => setAdjustOpen(false);
  const adjustmentTrimmed = adjustment.trim();
  const examples = (adjustExamples ?? []).map(e => e.trim()).filter(Boolean).slice(0, 3);
  const adjustPlaceholder = examples.length ? `例如: ${examples.join(' / ')}` : undefined;
  return (
    <div className="hitl-interrupt-card">
      <div className="hitl-interrupt-card__icon">⏸</div>
      <div className="hitl-interrupt-card__body">
        <p className="hitl-interrupt-card__message">
          <strong>请确认这批人群：</strong>
        </p>
        {segmentDescription && (
          <p className="hitl-interrupt-card__summary">{segmentDescription}</p>
        )}
        {typeof memberCount === 'number' && (
          <ul className="hitl-interrupt-card__meta">
            <li>人数：<strong>{memberCount.toLocaleString()}</strong></li>
          </ul>
        )}
        <p className="hitl-interrupt-card__prompt">
          {onAdjust
            ? '抽样明细见上方聊天记录。批准后将保存这批人群并同步到 Octopus；要改筛选条件点「调整条件」，想先聊聊或停止点「取消」。'
            : '抽样明细见上方聊天记录。批准后将保存这批人群并同步到 Octopus；想继续调整筛选条件，点「取消」接着聊。'}
        </p>
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" disabled={!active} onClick={onApprove}>批准</Button>
          {onAdjust && (
            <Button disabled={!active} onClick={() => { setAdjustment(''); setAdjustOpen(true); }}>
              调整条件
            </Button>
          )}
          {onViewAll && typeof memberCount === 'number' && memberCount > 20 && (
            <Button disabled={!active} onClick={onViewAll}>
              看全部人的信息
            </Button>
          )}
          <Button variant="link" disabled={!active} onClick={onReject}>取消</Button>
        </SpaceBetween>
      </div>

      {onAdjust && modalOpen && (
        <Modal
          visible
          onDismiss={closeModal}
          header="调整圈人条件"
          footer={(
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={closeModal}>取消</Button>
                <Button
                  variant="primary"
                  disabled={!adjustmentTrimmed}
                  onClick={() => { closeModal(); onAdjust(adjustmentTrimmed); }}
                >
                  🔍 重新查询
                </Button>
              </SpaceBetween>
            </Box>
          )}
        >
          <FormField label="用自然语言描述你想调整的条件">
            <Textarea
              value={adjustment}
              onChange={({ detail }) => setAdjustment(detail.value)}
              placeholder={adjustPlaceholder}
            />
          </FormField>
        </Modal>
      )}
    </div>
  );
}

// ─── Gate 2: Email Content batch approval ────────────────────────────────
//
// The card IS the batch review: aggregate counts for the whole batch, the ≤20-row
// sample, and every flagged recipient. The chat carries only a one-line lead-in,
// so the rows exist once, here. Field names follow the batch results file the
// agent copies them from (`ebr_status`, `portal_url`, `status`); the older
// `ebr_verdict` / `preview_link` / `preview_rows` / `ebr_pass|warn|hold` names are
// still read so gates settled before this contract keep rendering on replay.

export interface ContentPreviewRow {
  customer_id?: string;
  subject?: string;
  ebr_status?: string;
  ebr_verdict?: string;
  portal_url?: string;
  preview_link?: string;
  /** Generation status from the batch results — anything but `OK` failed. */
  status?: string;
  /** ABM contact-context columns, rendered only when some row carries them. */
  name?: string;
  company?: string;
  title?: string;
  email_masked?: string;
  cta_link?: string;
  [k: string]: unknown;
}

export interface ContentApprovalPanelProps {
  /** Frozen `content_plan_id` from the email-content-personalization skill. */
  contentPlanId?: string;
  /** The full audience count — not the number of rows shown. */
  totalCount?: number;
  /** Count per `ebr_status` across the whole batch, every bucket. */
  ebrHistogram?: Record<string, number>;
  /** Recipients whose generation failed. */
  failedCount?: number;
  sampleRows?: ContentPreviewRow[];
  flaggedRows?: ContentPreviewRow[];
  /** All flagged recipients, when more than `flaggedRows` carries. */
  flaggedTotal?: number;
  /** Set once the gate is answered: the card turns read-only and keeps its rows. */
  verdict?: 'approved' | 'rejected';
  /** Workspace-relative path of the full `batch_result.csv`; with `loadCsv`, enables the「全部 N 封」section. */
  batchCsvPath?: string;
  /** Fetches the CSV text for `batchCsvPath`. Injected so the panel stays presentational. */
  loadCsv?: (path: string) => Promise<string>;
  active: boolean;
  onApprove?: () => void;
  onReject?: () => void;
}

/** Map a gate call's raw arguments to panel props, reading both argument vintages. */
export function contentPanelPropsFromArgs(
  args: Record<string, unknown> | undefined,
): Omit<ContentApprovalPanelProps, 'active' | 'verdict' | 'onApprove' | 'onReject'> {
  const a = args ?? {};
  const num = (k: string) => (typeof a[k] === 'number' ? (a[k] as number) : undefined);
  const rows = (k: string) => (Array.isArray(a[k]) ? (a[k] as ContentPreviewRow[]) : undefined);
  let ebrHistogram: Record<string, number> | undefined;
  if (a.ebr_histogram && typeof a.ebr_histogram === 'object' && !Array.isArray(a.ebr_histogram)) {
    ebrHistogram = a.ebr_histogram as Record<string, number>;
  } else {
    const legacy: Record<string, number> = {};
    if (num('ebr_pass') !== undefined) legacy.pass = num('ebr_pass')!;
    if (num('ebr_warn') !== undefined) legacy.pass_with_warning = num('ebr_warn')!;
    if (num('ebr_hold') !== undefined) legacy.hold = num('ebr_hold')!;
    if (Object.keys(legacy).length > 0) ebrHistogram = legacy;
  }
  return {
    contentPlanId: typeof a.content_plan_id === 'string' ? a.content_plan_id : undefined,
    totalCount: num('total_count'),
    ebrHistogram,
    failedCount: num('failed_count'),
    sampleRows: rows('sample_rows') ?? rows('preview_rows'),
    flaggedRows: rows('flagged_rows'),
    flaggedTotal: num('flagged_total'),
    batchCsvPath: typeof a.batch_csv_path === 'string' && a.batch_csv_path ? a.batch_csv_path : undefined,
  };
}

/** Badges from email-content-personalization's batch-table spec. */
const EBR_BADGE: Record<string, string> = {
  pass: '✅ Pass',
  pass_with_warning: '⚠️ Pass with Warning',
  hold: '❌ Hold',
  error: '❌ Hold',
  pending: '⏳ Pending',
};

/** Histogram display order; unknown buckets follow in their own order. */
const EBR_ORDER = ['pass', 'pass_with_warning', 'hold', 'error', 'pending', 'missing'];

function ebrBadge(row: ContentPreviewRow): string {
  if (typeof row.status === 'string' && row.status !== 'OK') return '🚫 生成失败';
  const v = row.ebr_status ?? row.ebr_verdict;
  if (!v) return '—';
  return EBR_BADGE[v] ?? v;
}

/** `portal_url` is a same-origin path (`/?workspace=…`); absolute http(s) is also fine.
 *  Exported for the Step 3a preview card, which links the same kind of URL. */
export function safeHref(url: unknown): string | undefined {
  if (typeof url !== 'string') return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return undefined;
}

const ABM_COLUMNS: Array<{ key: keyof ContentPreviewRow; label: string }> = [
  { key: 'name', label: '姓名' },
  { key: 'company', label: '公司' },
  { key: 'title', label: '职位' },
  { key: 'email_masked', label: '邮箱' },
];

function ContentRowTable({ rows }: { rows: ContentPreviewRow[] }) {
  const pageCount = Math.max(1, Math.ceil(rows.length / CONTENT_PAGE_SIZE));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const pageRows = useMemo(() => {
    const start = (currentPageIndex - 1) * CONTENT_PAGE_SIZE;
    return rows.slice(start, start + CONTENT_PAGE_SIZE);
  }, [rows, currentPageIndex]);
  const rangeStart = (currentPageIndex - 1) * CONTENT_PAGE_SIZE + 1;
  const abm = ABM_COLUMNS.filter((c) => rows.some((r) => typeof r[c.key] === 'string'));
  const hasCta = rows.some((r) => safeHref(r.cta_link));
  return (
    <>
      <div className="hitl-interrupt-card__table-wrap">
        <table className="hitl-interrupt-card__preview-table">
          <thead>
            <tr>
              <th className="hitl-interrupt-card__col-index">#</th>
              <th className="hitl-interrupt-card__col-id">customer_id</th>
              {abm.map((c) => <th key={c.key}>{c.label}</th>)}
              <th>Subject</th>
              <th>EBR</th>
              <th>Preview</th>
              {hasCta && <th>CTA</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => {
              const preview = safeHref(r.portal_url ?? r.preview_link);
              const cta = safeHref(r.cta_link);
              return (
                <tr key={r.customer_id ?? `${currentPageIndex}-${i}`}>
                  <td className="hitl-interrupt-card__col-index">{rangeStart + i}</td>
                  <td className="hitl-interrupt-card__col-id"><code>{r.customer_id ?? '—'}</code></td>
                  {abm.map((c) => (
                    <td key={c.key} title={r[c.key] as string | undefined}>{(r[c.key] as string | undefined) ?? '—'}</td>
                  ))}
                  <td title={r.subject}>{r.subject ?? '—'}</td>
                  <td>{ebrBadge(r)}</td>
                  <td>{preview ? <WorkspaceFileLink href={preview}>📧 查看</WorkspaceFileLink> : '—'}</td>
                  {hasCta && <td>{cta ? <a href={cta} target="_blank" rel="noreferrer">🔗</a> : '—'}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="hitl-interrupt-card__pagination">
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={pageCount}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        </div>
      )}
    </>
  );
}

// ─── Gate 2: full batch_result.csv section ───────────────────────────────

type CsvFilter = 'all' | 'review' | 'failed';

const needsReview = (r: ContentPreviewRow) => r.status !== 'OK' || r.ebr_status !== 'pass';
const isFailed = (r: ContentPreviewRow) => r.status !== 'OK';

/** CSV text → rows keyed by header name; unknown columns ignored, blank lines skipped. */
function csvToRows(text: string): ContentPreviewRow[] {
  const table = parseCsv(text);
  if (table.length < 2) return [];
  const header = table[0].map((h) => h.trim());
  const col = (name: string) => header.indexOf(name);
  const idx = {
    customer_id: col('customer_id'),
    status: col('status'),
    subject: col('subject'),
    ebr_status: col('ebr_status'),
    portal_url: col('portal_url'),
  };
  const out: ContentPreviewRow[] = [];
  for (const cells of table.slice(1)) {
    if (cells.every((c) => c.trim() === '')) continue;
    const row: ContentPreviewRow = {};
    (Object.keys(idx) as Array<keyof typeof idx>).forEach((k) => {
      const i = idx[k];
      if (i >= 0 && cells[i] !== undefined) row[k] = cells[i];
    });
    out.push(row);
  }
  return out;
}

type CsvState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Loads + parses the batch CSV. `autoLoad` loads once per path; otherwise call `load()`.
 *
 * Once per PATH, not once per mount: the first path a card sees can be wrong (a
 * gate whose args were still streaming used to hand over a truncated one), and a
 * load locked to it left the card on its error after the real path arrived. Only
 * the latest path's answer is applied, so a slow stale request cannot overwrite it.
 */
function useBatchCsv(path: string | undefined, loadCsv: ((p: string) => Promise<string>) | undefined, autoLoad: boolean) {
  const [state, setState] = useState<CsvState>('idle');
  const [rows, setRows] = useState<ContentPreviewRow[]>([]);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<CsvFilter>('all');
  const enabled = !!path && !!loadCsv;
  const requested = useRef<string | undefined>(undefined);

  const load = () => {
    if (!path || !loadCsv) return;
    requested.current = path;
    const isCurrent = () => requested.current === path;
    setState('loading');
    loadCsv(path).then(
      (text) => {
        if (!isCurrent()) return;
        const parsed = csvToRows(text);
        if (parsed.length === 0) {
          setError('文件为空或无法解析');
          setState('error');
          return;
        }
        setRows(parsed);
        // Chosen once per load; the operator's later choice is never overridden.
        setFilter(parsed.some(needsReview) ? 'review' : 'all');
        setState('ready');
      },
      (err) => {
        if (!isCurrent()) return;
        setError(`无法加载全部明细：${err instanceof Error ? err.message : String(err)}`);
        setState('error');
      },
    );
  };

  useEffect(() => {
    if (autoLoad && enabled && requested.current !== path) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, enabled, path]);

  return { enabled, state, rows, error, filter, setFilter, load };
}

function BatchCsvSection({
  csv,
  totalCount,
}: {
  csv: ReturnType<typeof useBatchCsv>;
  totalCount?: number;
}) {
  const { state, rows, error, filter, setFilter, load } = csv;
  // Always opens expanded with the file auto-loaded — pending and settled alike (restored
  // history included): the CSV is the card's only detail list. Collapsing is the operator's.
  const [open, setOpen] = useState(true);
  const [query, setQuery] = useState('');

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    const next = !open;
    setOpen(next);
    // Load on first expand only; a failed load may be retried on the next expand.
    if (next && (state === 'idle' || state === 'error')) load();
  };

  const counts = useMemo(
    () => ({ all: rows.length, review: rows.filter(needsReview).length, failed: rows.filter(isFailed).length }),
    [rows],
  );
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'review' && !needsReview(r)) return false;
      if (filter === 'failed' && !isFailed(r)) return false;
      if (!q) return true;
      return (r.customer_id ?? '').toLowerCase().includes(q) || (r.subject ?? '').toLowerCase().includes(q);
    });
  }, [rows, filter, query]);

  const label = state === 'ready'
    ? `全部 ${rows.length.toLocaleString()} 封`
    : typeof totalCount === 'number' ? `全部 ${totalCount.toLocaleString()} 封` : '全部明细';
  const filters: Array<[CsvFilter, string]> = [['all', '全部'], ['review', '需复核'], ['failed', '生成失败']];

  return (
    <details className="hitl-interrupt-card__csv" open={open}>
      <summary onClick={toggle}>{label}</summary>
      {open && (
        <div className="hitl-interrupt-card__samples">
          {state === 'loading' && <p className="hitl-interrupt-card__prompt">加载中…</p>}
          {state === 'error' && (
            <div className="hitl-interrupt-card__prompt">
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <span>{error}</span>
                <Button onClick={load}>重试</Button>
              </SpaceBetween>
            </div>
          )}
          {state === 'ready' && (
            <>
              {typeof totalCount === 'number' && rows.length !== totalCount && (
                <p className="hitl-interrupt-card__prompt">
                  文件 {rows.length.toLocaleString()} 行，与全批 {totalCount.toLocaleString()} 封不一致
                </p>
              )}
              <div className="hitl-interrupt-card__csv-toolbar">
                <SpaceBetween direction="horizontal" size="xs">
                  {filters.map(([key, text]) => (
                    <Button
                      key={key}
                      variant={filter === key ? 'primary' : 'normal'}
                      onClick={() => setFilter(key)}
                    >
                      {`${text} ${counts[key].toLocaleString()}`}
                    </Button>
                  ))}
                  <Input
                    type="search"
                    value={query}
                    placeholder="搜索 customer_id / subject"
                    ariaLabel="搜索 customer_id / subject"
                    onChange={({ detail }) => setQuery(detail.value)}
                  />
                </SpaceBetween>
              </div>
              <ContentRowTable key={`${filter}|${query}`} rows={shown} />
            </>
          )}
        </div>
      )}
    </details>
  );
}

export function ContentApprovalPanel({
  contentPlanId,
  totalCount,
  ebrHistogram,
  failedCount,
  sampleRows,
  flaggedRows,
  flaggedTotal,
  verdict,
  batchCsvPath,
  loadCsv,
  active,
  onApprove,
  onReject,
}: ContentApprovalPanelProps) {
  const sample = sampleRows ?? [];
  const flagged = flaggedRows ?? [];
  const moreFlagged =
    typeof flaggedTotal === 'number' && flaggedTotal > flagged.length ? flaggedTotal - flagged.length : 0;
  const buckets = ebrHistogram
    ? Object.keys(ebrHistogram).sort((x, y) => {
        const ix = EBR_ORDER.indexOf(x);
        const iy = EBR_ORDER.indexOf(y);
        return (ix < 0 ? EBR_ORDER.length : ix) - (iy < 0 ? EBR_ORDER.length : iy);
      })
    : [];
  const pending = ebrHistogram?.pending ?? 0;
  const settled = verdict !== undefined;
  const csv = useBatchCsv(batchCsvPath, loadCsv, true);
  // The CSV table replaces the args lists only once it is actually up. Until then
  // (loading, failed, or no file) the args lists stay: 批准 is live the whole time,
  // so the flagged recipients must be on screen the whole time.
  const argTables = !csv.enabled || csv.state !== 'ready';
  // The gate no longer carries rows (only gates settled under the older contract
  // do), so the CSV is the only list of who is being approved. Until it is up —
  // or when there is no path at all — 批准 would be a blind approval, so it waits;
  // 取消 does not.
  const hasArgRows = flagged.length > 0 || sample.length > 0;
  const awaitingCsv = !settled && !hasArgRows && (!csv.enabled || csv.state !== 'ready');

  const details = (
    <>
      {flagged.length > 0 && (
        <div className="hitl-interrupt-card__samples">
          <p className="hitl-interrupt-card__samples-title">
            需复核 / 未通过 {flagged.length} 位{moreFlagged > 0 && `（另 +${moreFlagged} 位未列出）`}：
          </p>
          <ContentRowTable rows={flagged} />
        </div>
      )}
      {sample.length > 0 && (
        <div className="hitl-interrupt-card__samples">
          <p className="hitl-interrupt-card__samples-title">
            抽样 {sample.length} 封
            {typeof totalCount === 'number' && `（全批 ${totalCount.toLocaleString()} 封）`}：
          </p>
          <ContentRowTable rows={sample} />
        </div>
      )}
    </>
  );
  const hasDetails = argTables && (flagged.length > 0 || sample.length > 0);

  return (
    <div className={`hitl-interrupt-card${settled ? ' hitl-interrupt-card--settled' : ''}`}>
      <div className="hitl-interrupt-card__icon">
        {verdict === 'approved' ? '✅' : verdict === 'rejected' ? '❌' : '⏸'}
      </div>
      <div className="hitl-interrupt-card__body">
        <p className="hitl-interrupt-card__message">
          <strong>{settled ? '邮件内容审批' : '请审批邮件内容批次：'}</strong>
        </p>
        <ul className="hitl-interrupt-card__meta">
          {contentPlanId && (
            <li>Content Plan: <code>{contentPlanId}</code></li>
          )}
          {typeof totalCount === 'number' && (
            <li>全批：<strong>{totalCount.toLocaleString()}</strong> 封</li>
          )}
          {buckets.length > 0 && (
            <li>
              EBR：
              {buckets.map((b, i) => (
                <span key={b}>
                  {i > 0 && ' · '}
                  {EBR_BADGE[b] ?? b} {ebrHistogram![b].toLocaleString()}
                </span>
              ))}
            </li>
          )}
          {typeof failedCount === 'number' && failedCount > 0 && (
            <li>🚫 生成失败 {failedCount.toLocaleString()}</li>
          )}
        </ul>
        {!settled && pending > 0 && (
          <p className="hitl-interrupt-card__prompt">
            ⏳ 仍有 {pending.toLocaleString()} 封 EBR 检查未完成，这些邮件不应视为已通过。
          </p>
        )}
        {hasDetails && (settled
          ? (
            <details>
              <summary>审批时的明细</summary>
              {details}
            </details>
          )
          : details)}
        {csv.enabled && (
          <BatchCsvSection csv={csv} totalCount={totalCount} />
        )}
        {settled ? (
          <p className="hitl-interrupt-card__prompt hitl-interrupt-card__prompt--settled">
            {verdict === 'approved' ? '已批准' : '已取消'}
          </p>
        ) : (
          <>
            <p className="hitl-interrupt-card__prompt">
              {awaitingCsv
                ? !csv.enabled
                  ? '缺少批次明细文件（batch_result.csv），无法批准；请取消。'
                  : csv.state === 'error'
                    ? '明细加载失败，重试成功后才能批准；也可以取消。'
                    : '明细加载完成后才能批准。'
                : '确认后将创建 Octopus Program，是否继续？'}
            </p>
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="primary" disabled={!active || awaitingCsv} onClick={onApprove}>批准</Button>
              <Button variant="link" disabled={!active} onClick={onReject}>取消</Button>
            </SpaceBetween>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Generic: n-choice user approval (request_user_approval) ─────────────

export interface GenericApprovalOption {
  /** Stable short id the agent routes on (e.g. `retry` / `skip` / `continue` / `abort`).
   *  The agent MUST NOT parse `label` — always branch on `value`. */
  value: string;
  /** User-facing button text (may include emoji). */
  label: string;
  /** Optional visual hint. `danger` marks a destructive proceed (e.g. "忽略失败继续"). */
  kind?: 'primary' | 'secondary' | 'danger' | 'ghost';
}

export interface GenericApprovalPanelProps {
  /** Short header line (bold). */
  title?: string;
  /** Markdown body — cause / impact / guidance. */
  message?: string;
  /** 2–4 button choices; rendered left-to-right. Empty is a bug (agent forgot to pass options). */
  options: GenericApprovalOption[];
  /** Whether the run is still waiting on this approval — buttons disable when false. */
  active: boolean;
  /** Fires with the selected option's `value`. */
  onChoose: (value: string) => void;
}

/** Polaris Button has no `danger` variant; map our `kind` to what it accepts.
 *  We keep the semantic on the option so the emoji in `label` can signal
 *  danger visually (spec suggests ❌ / 🔴 prefixes for destructive options). */
function mapKindToVariant(kind: GenericApprovalOption['kind']): 'primary' | 'normal' | 'link' {
  if (kind === 'primary') return 'primary';
  if (kind === 'ghost') return 'link';
  // 'danger' and 'secondary' both render as normal; label emoji carries the danger cue.
  return 'normal';
}

export function GenericApprovalPanel({
  title,
  message,
  options,
  active,
  onChoose,
}: GenericApprovalPanelProps) {
  const safeOptions = Array.isArray(options) ? options : [];
  return (
    <div className="hitl-interrupt-card">
      <div className="hitl-interrupt-card__icon">⏸</div>
      <div className="hitl-interrupt-card__body">
        {title && (
          <p className="hitl-interrupt-card__message">
            <strong>{title}</strong>
          </p>
        )}
        {message && (
          <div className="hitl-interrupt-card__summary">
            <MarkdownRenderer markdown={message} />
          </div>
        )}
        {safeOptions.length > 0 ? (
          <SpaceBetween direction="horizontal" size="xs">
            {safeOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={mapKindToVariant(opt.kind)}
                disabled={!active}
                onClick={() => onChoose(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </SpaceBetween>
        ) : (
          <p className="hitl-interrupt-card__prompt">
            (agent 未提供选项 — 请刷新会话或让 agent 重新调用 request_user_approval)
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Settled variant: shared read-only card for status === Complete ──────
//
// Rendered by the same gate tools when the run has already been settled
// (user clicked the interactive card in a prior turn, or the session was
// rehydrated with a completed tool call). Uses the same card chrome but
// swaps the buttons for a verdict pill and drops the "prompt" call-to-
// action line. Keep the layout minimal so a long chat scroll doesn't turn
// into a wall of full-height approval cards.

export interface SettledApprovalCardProps {
  /** Bold header line — matches the interactive variant's title. */
  title: string;
  /** Optional one-line summary (e.g. segment description, content plan id). */
  subtitle?: string;
  /** Optional meta rows shown as a compact bullet list. */
  metaLines?: string[];
  /** Semantic outcome — drives the leading icon and pill copy. */
  verdict: 'approved' | 'rejected' | 'choice' | 'unknown';
  /** For `verdict === 'choice'` (generic tool), the selected option label. */
  choiceLabel?: string;
  /** Overrides the pill copy — for gates whose answer is not approve/reject/choice
   *  (the Step 3a preview card's `{action}`). */
  verdictText?: string;
}

export function SettledApprovalCard({
  title,
  subtitle,
  metaLines,
  verdict,
  choiceLabel,
  verdictText: verdictTextOverride,
}: SettledApprovalCardProps) {
  const icon = verdict === 'approved' ? '✅' : verdict === 'rejected' ? '❌' : '⚪';
  const verdictText = verdictTextOverride ?? (
    verdict === 'approved' ? '已批准'
    : verdict === 'rejected' ? '已取消'
    : verdict === 'choice' ? `已选择：${choiceLabel ?? '—'}`
    : '已处理');
  return (
    <div className="hitl-interrupt-card hitl-interrupt-card--settled">
      <div className="hitl-interrupt-card__icon">{icon}</div>
      <div className="hitl-interrupt-card__body">
        <p className="hitl-interrupt-card__message">
          <strong>{title}</strong>
        </p>
        {subtitle && (
          <p className="hitl-interrupt-card__summary">{subtitle}</p>
        )}
        {metaLines && metaLines.length > 0 && (
          <ul className="hitl-interrupt-card__meta">
            {metaLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}
        <p className="hitl-interrupt-card__prompt hitl-interrupt-card__prompt--settled">
          {verdictText}
        </p>
      </div>
    </div>
  );
}
